// "Tonight" on Today: whose turn it is and the overnight toilet trip plan.
// From late afternoon it leads with the night; in the day it's one quiet line.

import { AlarmClock, ArrowLeftRight, Moon, Sun, Users } from 'lucide-react';
import { useMemo } from 'react';
import { saveHousehold } from '../../data/actions.ts';
import { useEvents, useHousehold, useLiveStatus } from '../../data/hooks.ts';
import { nightTripPlan, onDuty, tonightAt, withSwap } from '../../domain/nightplan.ts';
import { inClockWindow } from '../../domain/time.ts';
import { navigate } from '../../app/router.ts';
import { Avatar } from '../../ui/bits.tsx';
import { Button, Card, CardHeader } from '../../ui/primitives.tsx';
import type { Person } from '../../domain/types.ts';
import { toast } from '../../ui/toast.tsx';

export function TonightCard() {
  const s = useLiveStatus(60_000);
  const household = useHousehold();
  const events = useEvents();
  const weeks = s.age?.weeks ?? 12;
  const plan = useMemo(() => nightTripPlan({ household, events, weeks, now: s.now }), [household, events, weeks, s.now]);
  const hasRota = !!household.rota && Object.values(household.rota).some((d) => d?.some(Boolean));
  const hour = new Date(s.now).getHours();
  const evening = hour >= 17 || inClockWindow(s.now, household.nightStart, household.nightEnd);
  if (!plan.time && !hasRota) return null;

  // Tomorrow morning's person, looked up at 8am tomorrow (or this morning, in the small hours).
  const tomorrow8 = new Date(s.now);
  if (hour >= 12) tomorrow8.setDate(tomorrow8.getDate() + 1);
  tomorrow8.setHours(8, 0, 0, 0);
  const morning = onDuty(household, 'morning', tomorrow8.getTime());
  const day = onDuty(household, 'day', s.now);
  // One tap: hand tonight to the next person (just tonight — the rota stays as it is).
  const next = household.people.length ? household.people[(household.people.findIndex((p) => p.id === plan.who?.id) + 1) % household.people.length] : undefined;
  const swapTonight = () => {
    if (!next) return;
    saveHousehold({ rotaSwaps: withSwap(household, 'night', tonightAt(household, s.now), next.id) });
    toast({ message: `${next.name}’s doing tonight`, detail: 'Just tonight — the rota is unchanged.', tone: 'good' });
  };
  // An accident to act on is a warning; a run of dry nights is good news; otherwise just a reminder.
  const adviceTone = plan.lastAccident && plan.advice.startsWith('Accident') ? 'warn' : plan.dryNights >= 3 ? 'good' : 'neutral';
  const setTime = (time: string) => {
    saveHousehold({ nightTrip: { ...household.nightTrip, time, off: false } });
    toast({ message: `Night trip moved to ${time}`, tone: 'good' });
  };

  if (!evening)
    return (
      <Card className="tonight tonight-compact">
        <div className="tonight-line">
          <Users aria-hidden />
          <span>
            {day ? (
              <>
                <strong>{day.name}</strong>’s on today
              </>
            ) : (
              'Today'
            )}
            {plan.time && plan.who && (
              <>
                {' · '}
                <strong>{plan.who.name}</strong> does the {plan.time} trip
                {morning?.id === plan.who.id && ' and the morning'}
              </>
            )}
            {plan.time && !plan.who && <> · night trip {plan.time}</>}
            {morning && morning.id !== plan.who?.id && (
              <>
                {' · '}
                <strong>{morning.name}</strong> up first
              </>
            )}
          </span>
          {plan.time && next && household.people.length > 1 && (
            <button type="button" className="link small" onClick={swapTonight}>
              Swap tonight
            </button>
          )}
          <button type="button" className="link small" onClick={() => navigate('/settings/household')}>
            Rota
          </button>
        </div>
      </Card>
    );

  return (
    <Card className="tonight">
      <CardHeader
        title="Tonight"
        icon={Moon}
        sub={plan.dryNights > 0 && !plan.proposeTime && !plan.proposeOff ? `${plan.dryNights} dry night${plan.dryNights === 1 ? '' : 's'} in a row` : undefined}
        action={
          <button type="button" className="link small" onClick={() => navigate('/settings/household')}>
            Rota & times
          </button>
        }
      />
      {/* The night as a timeline: time, what, who — one row each, all lined up. */}
      <ol className="tonight-plan">
        <li>
          <span className="tp-time">{household.nightStart}</span>
          <span className="tp-icon">
            <Moon aria-hidden />
          </span>
          <span className="tp-what">
            <strong>Bedtime wee</strong>
            <small>then lights out</small>
          </span>
          <span className="tp-who" />
        </li>
        {plan.time && (
          <li>
            <span className="tp-time">{plan.time}</span>
            <span className="tp-icon">
              <AlarmClock aria-hidden />
            </span>
            <span className="tp-what">
              <strong>Night trip</strong>
              <small>out, wee, straight back to bed</small>
            </span>
            <Who person={plan.who} />
          </li>
        )}
        <li>
          <span className="tp-time">{household.nightEnd}</span>
          <span className="tp-icon">
            <Sun aria-hidden />
          </span>
          <span className="tp-what">
            <strong>Up</strong>
            <small>straight out for a wee</small>
          </span>
          <Who person={morning} />
        </li>
      </ol>
      <div className={`tonight-advice tone-${adviceTone}`}>
        <p className="small">{plan.advice}</p>
        {(plan.proposeTime || plan.proposeOff || (plan.time && next && household.people.length > 1)) && (
          <div className="tonight-actions">
            {plan.proposeTime && (
              <Button size="sm" variant="primary" icon={AlarmClock} onClick={() => setTime(plan.proposeTime!)}>
                Use {plan.proposeTime}
              </Button>
            )}
            {plan.proposeOff && (
              <Button size="sm" variant="primary" onClick={() => (saveHousehold({ nightTrip: { ...household.nightTrip, off: true } }), toast({ message: 'No more night trips — well done, Arlo!', tone: 'good' }))}>
                Stop night trips
              </Button>
            )}
            {plan.time && next && household.people.length > 1 && (
              <Button size="sm" variant="soft" icon={ArrowLeftRight} onClick={swapTonight}>
                Swap: {next.name} takes tonight
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/** Whose turn, or a nudge to set the rota. */
function Who({ person }: { person?: Person }) {
  return person ? (
    <span className="tp-who">
      <Avatar person={person} size={24} />
      <span>{person.name}</span>
    </span>
  ) : (
    <span className="tp-who tp-unset">not set</span>
  );
}
